import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Box, Chip, Typography, Avatar, Button,
} from '@mui/material'
import { ShoppingBag as OrderIcon, AttachMoney as RevenueIcon, TrendingUp as AvgIcon, Phone as CallIcon } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../components/shared/PageHeader'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { ListToolbar } from '../components/shared/ListToolbar'
import { toast } from 'react-toastify'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { DataTable, type Column } from '../components/ui/DataTable'
import { themeColors } from '../app/layout/theme'
import { retentionApi } from '../lib/api/retentionApi'
import { usersAdminApi } from '../lib/api/usersAdminApi'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import type { OrderVerificationRecord, OrderPaymentStatus } from '../types/verificationTypes'

// ── Badge config ───────────────────────────────────────────────────────────────

const PAYMENT_CONFIG: Record<OrderPaymentStatus, { bg: string; color: string; border: string }> = {
  'Pending':  { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Partial':  { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Paid':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Refunded': { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
}

function PaymentBadge({ status }: { status: OrderPaymentStatus }) {
  const cfg = PAYMENT_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color,
        border: `1px solid ${cfg.border}`, fontSize: '11px', fontWeight: 600,
        '& .MuiChip-label': { px: '7px' }, whiteSpace: 'nowrap',
      }}
    />
  )
}

// ── Date tab definitions ───────────────────────────────────────────────────────

type DateTab = 'all' | 'today' | 'week' | 'month'

const DATE_TABS: { key: DateTab; label: string }[] = [
  { key: 'all',   label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'This Week' },
  { key: 'month', label: 'This Month' },
]

function isInRange(iso: string | null | undefined, tab: DateTab): boolean {
  if (tab === 'all' || !iso) return true
  const date = new Date(iso)
  const now = new Date()
  if (tab === 'today') {
    return date.toDateString() === now.toDateString()
  }
  if (tab === 'week') {
    const start = new Date(now)
    start.setDate(now.getDate() - now.getDay())
    start.setHours(0, 0, 0, 0)
    return date >= start
  }
  if (tab === 'month') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
  }
  return true
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}
function avatarColor(name: string) {
  return themeColors.primary
}
function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function RecentOrdersPage() {
  const navigate = useNavigate()
  const [search, setSearch]     = useState('')
  const [filters, setFilters]   = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-recent-orders-filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { ...EMPTY_UNIFIED_FILTERS, ...parsed }
      } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })

  useEffect(() => {
    sessionStorage.setItem('crm-recent-orders-filters', JSON.stringify(filters))
  }, [filters])

  const [dateTab, setDateTab]   = useState<DateTab>(() => {
    const saved = sessionStorage.getItem('crm-recent-orders-date-tab')
    if (saved && ['all', 'today', 'yesterday', 'this_week', 'this_month'].includes(saved)) {
      return saved as DateTab
    }
    return 'all'
  })

  useEffect(() => {
    sessionStorage.setItem('crm-recent-orders-date-tab', dateTab)
  }, [dateTab])

  const [page, setPage]         = useState(1)
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_recent_orders_page_size')
    return saved ? Number(saved) : 10
  })

  const apiParams = useMemo(() => ({
    search: search || undefined,
    dateTab,
    dateFrom: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    dateTo: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
    customerType: filters.checkboxes.customerType?.length ? filters.checkboxes.customerType[0] : undefined,
    warehouse: filters.checkboxes.warehouse?.length ? filters.checkboxes.warehouse[0] : undefined,
    orderedBy: filters.checkboxes.orderedBy?.length ? filters.checkboxes.orderedBy[0] : undefined,
    assignedUser: filters.checkboxes.assignedUser?.length ? filters.checkboxes.assignedUser[0] : filters.checkboxes.user?.length ? filters.checkboxes.user[0] : undefined,
    page,
    pageSize,
  }), [search, dateTab, filters, page, pageSize])

  // 1. Fetch real placed orders from dedicated recency-orders API (ultra-fast list API)
  const { data: recencyData, isLoading } = useQuery({
    queryKey: ['recency-orders-dedicated', apiParams],
    queryFn: () => retentionApi.getRecencyOrders(apiParams),
    staleTime: 30_000,
  })

  // 2. Fetch summary stats asynchronously in parallel
  const { data: statsData } = useQuery({
    queryKey: ['recency-orders-stats', apiParams],
    queryFn: () => retentionApi.getRecencyOrderStats(apiParams),
    staleTime: 30_000,
  })

  const retentionData = recencyData?.items ?? []
  const total = statsData?.total ?? recencyData?.total ?? retentionData.length
  const totalOrderValue = statsData?.totalOrderValue ?? 0
  const totalCalls = statsData?.totalCalls ?? 0
  const totalPages = recencyData?.totalPages ?? Math.max(1, Math.ceil(total / pageSize))

  const headerStats = useMemo(() => {
    const avgOrderValue = total > 0 ? totalOrderValue / total : 0
    return [
      {
        label: 'Total Order Value',
        value: fmtCurrency(totalOrderValue),
        icon: <RevenueIcon sx={{ fontSize: 18 }} />,
        color: '#059669',
      },
      {
        label: 'Total Orders',
        value: total.toLocaleString('en-IN'),
        icon: <OrderIcon sx={{ fontSize: 18 }} />,
        color: themeColors.primary,
      },
      {
        label: 'Average Order Value',
        value: fmtCurrency(avgOrderValue),
        icon: <AvgIcon sx={{ fontSize: 18 }} />,
        color: '#0284c7',
      },
      {
        label: 'Total Calls',
        value: totalCalls.toLocaleString('en-IN'),
        icon: <CallIcon sx={{ fontSize: 18 }} />,
        color: '#8b5cf6',
      },
    ]
  }, [totalOrderValue, total, totalCalls])

  const { data: masterCustomerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['master-customer-types-for-recency'],
    queryFn: () => getCustomerTypes(),
    staleTime: 10 * 60 * 1000,
  })

  const { data: masterWarehouses = [] } = useQuery({
    queryKey: ['master-warehouses-for-recency'],
    queryFn: () => retentionApi.getWarehouses(),
    staleTime: 10 * 60 * 1000,
  })

  const { data: usersData } = useQuery({
    queryKey: ['all-active-users-for-recency-filter'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000, status: 'Active' }),
    staleTime: 10 * 60 * 1000,
  })

  const systemUserNames = useMemo(() => {
    return (usersData?.items ?? []).map(u => u.name).filter(Boolean)
  }, [usersData])

  const validCustomerTypes = useMemo(() => {
    return masterCustomerTypes.map(c => c.name).filter(Boolean)
  }, [masterCustomerTypes])

  const validWarehouses = useMemo(() => {
    return masterWarehouses.map(w => w.warehouseName).filter(Boolean)
  }, [masterWarehouses])

  const filterDefs: FilterDef[] = useMemo(() => [
    {
      type: 'checkbox' as const,
      key: 'customerType',
      label: 'Customer Type',
      options: validCustomerTypes.map(ct => ({ label: ct, value: ct })),
    },
    {
      type: 'checkbox' as const,
      key: 'warehouse',
      label: 'Warehouse',
      options: validWarehouses.map(wh => ({ label: wh, value: wh })),
    },
    {
      type: 'checkbox' as const,
      key: 'orderedBy',
      label: 'Ordered By',
      options: systemUserNames.map(u => ({ label: u, value: u })),
    },
    {
      type: 'checkbox' as const,
      key: 'assignedUser',
      label: 'Assigned User',
      options: systemUserNames.map(u => ({ label: u, value: u })),
    },
    { type: 'date', label: 'Order Date' },
  ], [validCustomerTypes, validWarehouses, systemUserNames])

  const items = useMemo(() => {
    return retentionData.map(r => ({
      id: r.id,
      orderId: r.orderId || '—',
      outletId: r.outletId || '—',
      customerName: r.customerName || 'Unknown Customer',
      companyName: r.outletName || '—',
      customerType: r.customerType || '—',
      customerTypeId: r.customerTypeId,
      warehouseName: r.warehouseName || '—',
      orderValue: typeof r.orderValue === 'string' ? (parseFloat(r.orderValue) || 0) : (r.orderValue || 0),
      assignedUser: r.ownerName || 'Unassigned',
      orderedByUser: r.orderedByName || '—',
      createdDate: r.orderedAt || r.createdAt,
      orderDate: r.orderedAt || r.createdAt,
      email: r.email || undefined,
      mobile: r.mobile || undefined,
    }))
  }, [retentionData])

  const hasFilters = countActiveFilters(filters) > 0 || !!search || dateTab !== 'all'

  function clearFilters() {
    setSearch('')
    setFilters(EMPTY_UNIFIED_FILTERS)
    setDateTab('all')
    setPage(1)
  }

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columns: Column[] = [
    {
      id: 'orderId',
      label: 'Order ID',
      sortable: true,
      render: (_, row: any) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.link, fontWeight: 700, fontFamily: 'monospace' }}>
          {row.orderId}
        </Typography>
      ),
    },
    {
      id: 'outletId',
      label: 'Outlet ID',
      sortable: true,
      render: (_, row: any) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600, fontFamily: 'monospace' }}>
          {row.outletId}
        </Typography>
      ),
    },
    {
      id: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (_, row: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '10px', fontWeight: 700, bgcolor: avatarColor(row.customerName) }}>
            {initials(row.customerName)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary, lineHeight: 1.2 }}>
              {row.customerName}
            </Typography>
            {/* {row.email && (
              <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, lineHeight: 1.2 }}>
                {row.email}
              </Typography>
            )} */}
          </Box>
        </Box>
      ),
    },
    // {
    //   id: 'companyName',
    //   label: 'Company',
    //   sortable: true,
    //   render: (_, row: any) => (
    //     <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
    //       {row.companyName || '—'}
    //     </Typography>
    //   ),
    // },
    {
      id: 'customerType',
      label: 'Customer Type',
      sortable: true,
      render: (_, row: any) => (
        <Chip
          label={row.customerType}
          size="small"
          sx={{
            height: 20, borderRadius: '4px', bgcolor: '#EDE7FF',
            color: themeColors.primary, fontSize: '11px', fontWeight: 600,
            '& .MuiChip-label': { px: '8px' },
          }}
        />
      ),
    },
    {
      id: 'warehouseName',
      label: 'Warehouse',
      sortable: true,
      render: (_, row: any) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {row.warehouseName || '—'}
        </Typography>
      ),
    },
    {
      id: 'orderValue',
      label: 'Order Value',
      sortable: true,
      align: 'right' as const,
      render: (_, row: any) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
            {fmtCurrency(row.orderValue)}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'orderDate',
      label: 'Order Date',
      sortable: true,
      render: (_, row: any) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {formatDate(row.orderDate ?? row.createdDate)}
        </Typography>
      ),
    },
    {
      id: 'orderedByUser',
      label: 'Ordered By',
      sortable: true,
      render: (_, row: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {row.orderedByUser !== '—' && (
            <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(row.orderedByUser) }}>
              {initials(row.orderedByUser)}
            </Avatar>
          )}
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: row.orderedByUser !== '—' ? 600 : 400 }}>
            {row.orderedByUser}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'assignedUser',
      label: 'Assignee',
      sortable: true,
      render: (_, row: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {row.assignedUser !== 'Unassigned' && (
            <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(row.assignedUser) }}>
              {initials(row.assignedUser)}
            </Avatar>
          )}
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
            {row.assignedUser}
          </Typography>
        </Box>
      ),
    },
  ]

  const [isExporting, setIsExporting] = useState(false)

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true)
      await retentionApi.exportRecencyOrders(apiParams)
      toast.success('Recency orders exported successfully')
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to export recency orders')
    } finally {
      setIsExporting(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Recency Dashboard"
        breadcrumb="CRM"
        stats={headerStats}
        actions={
          <PageActionButtons
            onExport={handleExportToExcel}
          />
        }
      />

      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search by order ID, customer, company, product..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          extraActions={
            <>
              {/* ── Date quick-tabs inline in toolbar ── */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {DATE_TABS.map(tab => (
                  <Button
                    key={tab.key}
                    size="small"
                    onClick={() => { setDateTab(tab.key); setPage(1) }}
                    sx={{
                      fontSize: '12px',
                      height: 38,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: dateTab === tab.key ? 700 : 500,
                      color: dateTab === tab.key ? themeColors.primary : themeColors.text.secondary,
                      bgcolor: dateTab === tab.key ? `${themeColors.primary}12` : 'transparent',
                      border: dateTab === tab.key ? `1px solid ${themeColors.primary}40` : '1px solid transparent',
                      px: 1.5,
                      '&:hover': {
                        bgcolor: `${themeColors.primary}08`,
                        border: `1px solid ${themeColors.primary}30`,
                      },
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Box>

              {hasFilters && (
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ fontSize: '12px', height: 38, textTransform: 'none', color: themeColors.text.secondary, borderRadius: '8px' }}
                >
                  Clear Filters
                </Button>
              )}
            </>
          }
        />
      </Box>

      {/* ── Scrollable table ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable
            columns={columns}
            rows={items}
            isLoading={isLoading}
            selectable={false}
            showRowNumbers={false}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/recency-orders/${row.id}`)}
            emptyIcon={<OrderIcon />}
            emptyMessage={hasFilters ? 'No orders match your filters' : 'No recent orders found'}
            emptyHint={hasFilters ? 'Try adjusting your filters or date range' : 'Placed orders will appear here'}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              itemLabel: 'orders',
              onPageChange: setPage,
              onPageSizeChange: (s) => {
                setPageSize(s)
                localStorage.setItem('crm_recent_orders_page_size', String(s))
                setPage(1)
              },
              pageSizeOptions: [10, 20],
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
